import React, { useState } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { 
  FormControl, 
  FormLabel, 
  TextField, 
  Select as MuiSelect, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  Switch as MuiSwitch,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useQwanta } from "qwanyx";

export const Switch = ({
  name,
  qwantumId,
  checked = false,
  inputName,
  onSave,
  hidden,
  style,
  // Switch specific
  disabled = false,
  disableRipple = false,
  size = "medium",
  color = "primary",
  edge = false,
  value,
  // Label
  labelText = "",
  labelPlacement = "end",
  labelFontSize = 14,
  labelBold = false,
  labelItalic = false,
  fontFamily = "inherit",
  // Dimensions
  width = "auto",
  maxWidth,
  minWidth,
  flex,
  flexGrow,
  flexShrink,
  flexBasis,
  // Position
  position = 'relative',
  top,
  left,
  right,
  bottom,
  unit = 'px',
  lockedEdges = { top: false, left: false, right: false, bottom: false },
  // Styling
  backgroundColor,
  borderRadius,
  padding,
  cssClass,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Use useQwanta with proper conditional handling
  const [persistedValue, setPersistedValue] = useQwanta(qwantumId || "");
  const currentValue = (qwantumId && persistedValue !== null) ? persistedValue : checked;

  if (hidden) return null;

  const handleChange = (event) => {
    const newChecked = event.target.checked;
    
    // Update CraftJS props when in editor mode
    if (enabled) {
      setProp(props => props.checked = newChecked);
    }
    
    // Handle persistence with safety checks
    if (qwantumId && qwantumId.trim() && setPersistedValue) {
      // Use requestAnimationFrame to defer the persistence call
      requestAnimationFrame(() => {
        try {
          setPersistedValue(qwantumId, newChecked);
        } catch (error) {
          console.warn('Persistence failed:', error);
        }
      });
    }
    
    if (onSave) {
      onSave(newChecked);
    }
  };

  // Selection styling
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const getPositionStyle = () => {
    if (position === 'absolute' || position === 'relative') {
      const positionStyle = { position };
      
      if (lockedEdges.top && top !== undefined) {
        positionStyle.top = `${top}${unit}`;
      }
      if (lockedEdges.left && left !== undefined) {
        positionStyle.left = `${left}${unit}`;
      }
      if (lockedEdges.right && right !== undefined) {
        positionStyle.right = `${right}${unit}`;
      }
      if (lockedEdges.bottom && bottom !== undefined) {
        positionStyle.bottom = `${bottom}${unit}`;
      }
      
      return positionStyle;
    }
    return { position };
  };

  const formatSize = (value) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  const switchComponent = (
    <MuiSwitch
      checked={currentValue}
      onChange={handleChange}
      disabled={disabled}
      disableRipple={disableRipple}
      size={size}
      color={color}
      edge={edge || undefined}
      name={inputName}
      value={value}
      inputProps={{ 'aria-label': labelText || 'switch' }}
    />
  );

  const content = labelText ? (
    <FormControlLabel
      control={switchComponent}
      label={labelText}
      labelPlacement={labelPlacement}
      style={{
        fontSize: `${labelFontSize}px`,
        fontFamily: fontFamily,
        fontWeight: labelBold ? 'bold' : 'normal',
        fontStyle: labelItalic ? 'italic' : 'normal',
        color: '#333',
        margin: 0
      }}
    />
  ) : switchComponent;

  return (
    <div 
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      className={cssClass}
      style={{
        transition: 'all 0.2s ease',
        display: 'inline-flex',
        alignItems: 'center',
        width: formatSize(width),
        maxWidth: formatSize(maxWidth),
        minWidth: formatSize(minWidth),
        flex: flex,
        flexGrow: flexGrow,
        flexShrink: flexShrink,
        flexBasis: flexBasis,
        boxSizing: 'border-box',
        backgroundColor: backgroundColor,
        borderRadius: borderRadius ? `${borderRadius}px` : undefined,
        padding: padding ? `${padding}px` : undefined,
        ...getPositionStyle(),
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {content}
    </div>
  );
};

const SwitchSettings = () => {
  const { 
    actions: { setProp }, 
    name, qwantumId, checked, inputName, disabled, disableRipple, size, color, edge, value,
    labelText, labelPlacement, labelFontSize, labelBold, labelItalic, fontFamily,
    width, maxWidth, minWidth, flex, flexGrow, flexShrink, flexBasis,
    position, top, left, right, bottom, unit, lockedEdges,
    backgroundColor, borderRadius, padding, cssClass
  } = useNode((node) => ({
    name: node.data.props.name,
    qwantumId: node.data.props.qwantumId,
    checked: node.data.props.checked,
    inputName: node.data.props.inputName,
    disabled: node.data.props.disabled,
    disableRipple: node.data.props.disableRipple,
    size: node.data.props.size,
    color: node.data.props.color,
    edge: node.data.props.edge,
    value: node.data.props.value,
    labelText: node.data.props.labelText,
    labelPlacement: node.data.props.labelPlacement,
    labelFontSize: node.data.props.labelFontSize,
    labelBold: node.data.props.labelBold,
    labelItalic: node.data.props.labelItalic,
    fontFamily: node.data.props.fontFamily,
    width: node.data.props.width,
    maxWidth: node.data.props.maxWidth,
    minWidth: node.data.props.minWidth,
    flex: node.data.props.flex,
    flexGrow: node.data.props.flexGrow,
    flexShrink: node.data.props.flexShrink,
    flexBasis: node.data.props.flexBasis,
    position: node.data.props.position,
    top: node.data.props.top,
    left: node.data.props.left,
    right: node.data.props.right,
    bottom: node.data.props.bottom,
    unit: node.data.props.unit,
    lockedEdges: node.data.props.lockedEdges,
    backgroundColor: node.data.props.backgroundColor,
    borderRadius: node.data.props.borderRadius,
    padding: node.data.props.padding,
    cssClass: node.data.props.cssClass
  }));

  const switchColors = [
    { value: 'default', label: 'Default' },
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
    { value: 'error', label: 'Error' },
    { value: 'info', label: 'Info' },
    { value: 'success', label: 'Success' },
    { value: 'warning', label: 'Warning' }
  ];

  const switchSizes = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' }
  ];

  const edgeOptions = [
    { value: false, label: 'None' },
    { value: 'start', label: 'Start' },
    { value: 'end', label: 'End' }
  ];

  const labelPlacements = [
    { value: 'end', label: 'End (Right)' },
    { value: 'start', label: 'Start (Left)' },
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' }
  ];

  const fontFamilies = [
    { value: 'inherit', label: 'Inherit' },
    { value: 'Arial, sans-serif', label: 'Arial' },
    { value: 'Helvetica, sans-serif', label: 'Helvetica' },
    { value: "'Times New Roman', serif", label: 'Times New Roman' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: "'Courier New', monospace", label: 'Courier New' },
    { value: 'Verdana, sans-serif', label: 'Verdana' }
  ];

  const handleFlexChange = (prop, value) => {
    if (value === '') {
      setProp(props => props[prop] = undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setProp(props => props[prop] = numValue);
      }
    }
  };

  return (
    <div>
      {/* Basic Properties */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Basic Properties</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Component Name</FormLabel>
            <TextField
              value={name || ''}
              onChange={(e) => setProp(props => props.name = e.target.value)}
              size="small"
              placeholder="Enter component name"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Input Name</FormLabel>
            <TextField
              value={inputName || ''}
              onChange={(e) => setProp(props => props.inputName = e.target.value)}
              size="small"
              placeholder="Enter input name attribute"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Quantum ID</FormLabel>
            <TextField
              value={qwantumId || ''}
              onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
              size="small"
              placeholder="Enter quantum ID for persistence"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Value</FormLabel>
            <TextField
              value={value || ''}
              onChange={(e) => setProp(props => props.value = e.target.value)}
              size="small"
              placeholder="Enter switch value"
            />
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setProp(props => props.checked = e.target.checked)}
              />
            }
            label="Checked"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={disabled}
                onChange={(e) => setProp(props => props.disabled = e.target.checked)}
              />
            }
            label="Disabled"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={disableRipple}
                onChange={(e) => setProp(props => props.disableRipple = e.target.checked)}
              />
            }
            label="Disable Ripple"
          />

          <FormControl fullWidth margin="normal">
            <FormLabel>CSS Class</FormLabel>
            <TextField
              value={cssClass || ''}
              onChange={(e) => setProp(props => props.cssClass = e.target.value)}
              size="small"
              placeholder="Enter CSS class names"
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Material UI Styling */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Material UI Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Color</FormLabel>
            <MuiSelect
              value={color || 'primary'}
              onChange={(e) => setProp(props => props.color = e.target.value)}
              size="small"
            >
              {switchColors.map(colorOption => (
                <MenuItem key={colorOption.value} value={colorOption.value}>{colorOption.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Size</FormLabel>
            <MuiSelect
              value={size || 'medium'}
              onChange={(e) => setProp(props => props.size = e.target.value)}
              size="small"
            >
              {switchSizes.map(sizeOption => (
                <MenuItem key={sizeOption.value} value={sizeOption.value}>{sizeOption.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Edge</FormLabel>
            <MuiSelect
              value={edge || false}
              onChange={(e) => setProp(props => props.edge = e.target.value)}
              size="small"
            >
              {edgeOptions.map(edgeOption => (
                <MenuItem key={edgeOption.value} value={edgeOption.value}>{edgeOption.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Label Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Label Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Label Text</FormLabel>
            <TextField
              value={labelText || ''}
              onChange={(e) => setProp(props => props.labelText = e.target.value)}
              size="small"
              placeholder="Enter label text"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Label Placement</FormLabel>
            <MuiSelect
              value={labelPlacement || 'end'}
              onChange={(e) => setProp(props => props.labelPlacement = e.target.value)}
              size="small"
            >
              {labelPlacements.map(placement => (
                <MenuItem key={placement.value} value={placement.value}>{placement.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Label Font Size</FormLabel>
            <TextField
              value={labelFontSize || 14}
              onChange={(e) => setProp(props => props.labelFontSize = parseInt(e.target.value) || 14)}
              size="small"
              type="number"
              inputProps={{ min: 8, max: 72 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Font Family</FormLabel>
            <MuiSelect
              value={fontFamily || 'inherit'}
              onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
              size="small"
            >
              {fontFamilies.map(font => (
                <MenuItem key={font.value} value={font.value}>{font.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={labelBold}
                onChange={(e) => setProp(props => props.labelBold = e.target.checked)}
              />
            }
            label="Bold label"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={labelItalic}
                onChange={(e) => setProp(props => props.labelItalic = e.target.checked)}
              />
            }
            label="Italic label"
          />
        </AccordionDetails>
      </Accordion>

      {/* Layout & Positioning */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Layout & Positioning</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Width</FormLabel>
            <TextField
              value={width || ''}
              onChange={(e) => setProp(props => props.width = e.target.value)}
              size="small"
              placeholder="auto, 100%, 300px, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Max Width</FormLabel>
            <TextField
              value={maxWidth || ''}
              onChange={(e) => setProp(props => props.maxWidth = e.target.value)}
              size="small"
              placeholder="400px, 50%, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Min Width</FormLabel>
            <TextField
              value={minWidth || ''}
              onChange={(e) => setProp(props => props.minWidth = e.target.value)}
              size="small"
              placeholder="100px, 20%, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Position</FormLabel>
            <MuiSelect
              value={position || 'relative'}
              onChange={(e) => setProp(props => props.position = e.target.value)}
              size="small"
            >
              <MenuItem value="relative">Relative</MenuItem>
              <MenuItem value="absolute">Absolute</MenuItem>
            </MuiSelect>
          </FormControl>

          {(position === 'absolute' || position === 'relative') && (
            <>
              <FormControl fullWidth margin="normal">
                <FormLabel>Unit</FormLabel>
                <ToggleButtonGroup
                  value={unit}
                  exclusive
                  onChange={(e, value) => value && setProp(props => props.unit = value)}
                  size="small"
                >
                  <ToggleButton value="px">px</ToggleButton>
                  <ToggleButton value="%">%</ToggleButton>
                </ToggleButtonGroup>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Position Controls</FormLabel>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.top || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, top: e.target.checked })}
                      />
                    }
                    label="Top"
                  />
                  <TextField
                    value={top || ''}
                    onChange={(e) => setProp(props => props.top = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.top}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.left || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, left: e.target.checked })}
                      />
                    }
                    label="Left"
                  />
                  <TextField
                    value={left || ''}
                    onChange={(e) => setProp(props => props.left = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.left}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.right || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, right: e.target.checked })}
                      />
                    }
                    label="Right"
                  />
                  <TextField
                    value={right || ''}
                    onChange={(e) => setProp(props => props.right = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.right}
                    style={{ width: '80px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lockedEdges?.bottom || false}
                        onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, bottom: e.target.checked })}
                      />
                    }
                    label="Bottom"
                  />
                  <TextField
                    value={bottom || ''}
                    onChange={(e) => setProp(props => props.bottom = parseFloat(e.target.value) || 0)}
                    size="small"
                    type="number"
                    disabled={!lockedEdges?.bottom}
                    style={{ width: '80px' }}
                  />
                </div>
              </FormControl>
            </>
          )}

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex</FormLabel>
            <TextField
              value={flex || ''}
              onChange={(e) => handleFlexChange('flex', e.target.value)}
              size="small"
              placeholder="1, 0 1 auto, or leave empty"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Grow</FormLabel>
            <TextField
              value={flexGrow || ''}
              onChange={(e) => handleFlexChange('flexGrow', e.target.value)}
              size="small"
              placeholder="0, 1, 2, or leave empty"
              type="number"
              inputProps={{ min: 0, step: 1 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Shrink</FormLabel>
            <TextField
              value={flexShrink || ''}
              onChange={(e) => handleFlexChange('flexShrink', e.target.value)}
              size="small"
              placeholder="1, 0, or leave empty"
              type="number"
              inputProps={{ min: 0, step: 1 }}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Flex Basis</FormLabel>
            <TextField
              value={flexBasis || ''}
              onChange={(e) => setProp(props => props.flexBasis = e.target.value || undefined)}
              size="small"
              placeholder="auto, 200px, 50%, or leave empty"
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Container Styling */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Container Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal" component="fieldset">
            <FormLabel component="legend">Background Color</FormLabel>
            <input
              type="color"
              value={backgroundColor || '#ffffff'}
              onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
            />
          </FormControl>

          <FormControl size="small" component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">Border Radius</FormLabel>
            <Slider
              value={borderRadius || 0}
              step={1}
              min={0}
              max={50}
              valueLabelDisplay="auto"
              onChange={(_, value) => {
                setProp(props => props.borderRadius = value);
              }}
            />
          </FormControl>

          <FormControl size="small" component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">Padding</FormLabel>
            <Slider
              value={padding || 0}
              step={1}
              min={0}
              max={50}
              valueLabelDisplay="auto"
              onChange={(_, value) => {
                setProp(props => props.padding = value);
              }}
            />
          </FormControl>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

Switch.craft = {
  props: {
    name: "",
    qwantumId: "",
    checked: false,
    inputName: "",
    disabled: false,
    disableRipple: false,
    size: "medium",
    color: "primary",
    edge: false,
    value: "",
    labelText: "",
    labelPlacement: "end",
    labelFontSize: 14,
    labelBold: false,
    labelItalic: false,
    fontFamily: "inherit",
    width: "auto",
    maxWidth: null,
    minWidth: null,
    flex: null,
    flexGrow: null,
    flexShrink: null,
    flexBasis: null,
    position: 'relative',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    unit: 'px',
    lockedEdges: { top: false, left: false, right: false, bottom: false },
    backgroundColor: '',
    borderRadius: 0,
    padding: 0,
    cssClass: ''
  },
  related: {
    settings: SwitchSettings
  }
};